import React from 'react';
import { Box, Container, Typography, Paper } from '@mui/material';
import { Link } from 'react-router-dom';

const PrivacyPolicyPage: React.FC = () => {
  return (
    <Box
      sx={{
        minHeight: '100vh',
        backgroundColor: '#f5f5f5',
        padding: '2rem 0',
      }}
    >
      <Container maxWidth="md">
        <Paper
          elevation={3}
          sx={{
            padding: '3rem',
            backgroundColor: 'white',
            borderRadius: '8px',
          }}
        >
          <Typography
            variant="h3"
            component="h1"
            sx={{
              fontSize: '2rem',
              fontWeight: 600,
              color: '#333',
              marginBottom: '2rem',
              textAlign: 'center',
            }}
          >
            Privacy Policy
          </Typography>

          <Typography
            variant="body2"
            sx={{
              color: '#666',
              marginBottom: '2rem',
              fontStyle: 'italic',
            }}
          >
            Last updated: {new Date().toLocaleDateString()}
          </Typography>

          <Box sx={{ marginBottom: '2rem' }}>
            <Typography
              variant="h5"
              component="h2"
              sx={{
                fontSize: '1.25rem',
                fontWeight: 600,
                color: '#2c5aa0',
                marginBottom: '1rem',
              }}
            >
              1. Information We Collect
            </Typography>
            <Typography
              variant="body1"
              sx={{
                lineHeight: 1.8,
                color: '#555',
                marginBottom: '1rem',
                textAlign: 'justify',
              }}
            >
              When you use our service, we collect the following information:
            </Typography>
            <Typography
              variant="body1"
              component="ul"
              sx={{
                lineHeight: 1.8,
                color: '#555',
                marginLeft: '2rem',
                marginBottom: '1rem',
              }}
            >
              <li>Account information: email address, display name, and authentication credentials when you create an account</li>
              <li>Audio files: files you upload to our service for processing and storage, including files selected from Google Drive</li>
              <li>Google Drive data: when you authorize Google Drive integration (using the <code>drive.file</code> scope), we access only files you explicitly select through the Google Drive file picker or that the app creates, including file metadata (name, ID, MIME type) and file content for selected audio files</li>
              <li>Usage data: information about how you interact with our service, including snippets you create and audio file metadata</li>
              <li>Technical data: IP address, browser type, device information, and other technical details necessary for service operation</li>
            </Typography>
          </Box>

          <Box sx={{ marginBottom: '2rem' }}>
            <Typography
              variant="h5"
              component="h2"
              sx={{
                fontSize: '1.25rem',
                fontWeight: 600,
                color: '#2c5aa0',
                marginBottom: '1rem',
              }}
            >
              2. How We Use Your Information
            </Typography>
            <Typography
              variant="body1"
              sx={{
                lineHeight: 1.8,
                color: '#555',
                marginBottom: '1rem',
                textAlign: 'justify',
              }}
            >
              We use the information we collect to:
            </Typography>
            <Typography
              variant="body1"
              component="ul"
              sx={{
                lineHeight: 1.8,
                color: '#555',
                marginLeft: '2rem',
                marginBottom: '1rem',
              }}
            >
              <li>Provide, maintain, and improve our audio processing services</li>
              <li>Authenticate your identity and manage your account</li>
              <li>Store and process your uploaded audio files and created snippets</li>
              <li>Enable integration with third-party services (such as Google Drive) when you authorize them</li>
              <li>Respond to your requests, comments, and questions</li>
              <li>Send you service-related communications and updates</li>
            </Typography>
          </Box>

          <Box sx={{ marginBottom: '2rem' }}>
            <Typography
              variant="h5"
              component="h2"
              sx={{
                fontSize: '1.25rem',
                fontWeight: 600,
                color: '#2c5aa0',
                marginBottom: '1rem',
              }}
            >
              3. Data Storage and Security
            </Typography>
            <Typography
              variant="body1"
              sx={{
                lineHeight: 1.8,
                color: '#555',
                marginBottom: '1rem',
                textAlign: 'justify',
              }}
            >
              We implement appropriate technical and organizational measures to protect your personal information and audio files. However, no method of transmission over the internet or electronic storage is 100% secure. While we strive to use commercially acceptable means to protect your data, we cannot guarantee absolute security.
            </Typography>
            <Typography
              variant="body1"
              sx={{
                lineHeight: 1.8,
                color: '#555',
                marginBottom: '1rem',
                textAlign: 'justify',
              }}
            >
              Your audio files and associated data are stored securely and are only accessible to you and authorized service administrators for maintenance purposes.
            </Typography>
          </Box>

          <Box sx={{ marginBottom: '2rem' }}>
            <Typography
              variant="h5"
              component="h2"
              sx={{
                fontSize: '1.25rem',
                fontWeight: 600,
                color: '#2c5aa0',
                marginBottom: '1rem',
              }}
            >
              4. Third-Party Services
            </Typography>
            <Typography
              variant="body1"
              sx={{
                lineHeight: 1.8,
                color: '#555',
                marginBottom: '1rem',
                textAlign: 'justify',
              }}
            >
              Our service integrates with third-party services, including:
            </Typography>
            <Typography
              variant="body1"
              component="ul"
              sx={{
                lineHeight: 1.8,
                color: '#555',
                marginLeft: '2rem',
                marginBottom: '1rem',
              }}
            >
              <li><strong>Google Services:</strong> We use Google authentication and Google Drive integration. When you authorize these services, Google's privacy policy applies to data shared with Google.</li>
              <li><strong>Firebase:</strong> We use Firebase for authentication and user management. Firebase's privacy policy applies to data processed through Firebase.</li>
            </Typography>
            <Typography
              variant="body1"
              sx={{
                lineHeight: 1.8,
                color: '#555',
                marginBottom: '1rem',
                textAlign: 'justify',
              }}
            >
              We are not responsible for the privacy practices of these third-party services. We encourage you to review their privacy policies.
            </Typography>
          </Box>

          <Box sx={{ marginBottom: '2rem' }}>
            <Typography
              variant="h5"
              component="h2"
              sx={{
                fontSize: '1.25rem',
                fontWeight: 600,
                color: '#2c5aa0',
                marginBottom: '1rem',
              }}
            >
              4.1. Google Drive Integration
            </Typography>
            <Typography
              variant="body1"
              sx={{
                lineHeight: 1.8,
                color: '#555',
                marginBottom: '1rem',
                textAlign: 'justify',
              }}
            >
              Our service offers integration with Google Drive to allow you to select and use audio files stored in your Google Drive account. This integration requires you to authorize our application to access your Google Drive.
            </Typography>
            <Typography
              variant="body1"
              sx={{
                lineHeight: 1.8,
                color: '#555',
                marginBottom: '1rem',
                textAlign: 'justify',
                fontWeight: 600,
              }}
            >
              What Access We Request:
            </Typography>
            <Typography
              variant="body1"
              component="ul"
              sx={{
                lineHeight: 1.8,
                color: '#555',
                marginLeft: '2rem',
                marginBottom: '1rem',
              }}
            >
              <li><strong>Limited access via <code>drive.file</code>:</strong> We request access using the <code>drive.file</code> scope. This allows our application to access only files that it creates or that you explicitly open or select through the Google Drive file picker. We do not have access to your entire Google Drive.</li>
              <li><strong>What we access:</strong> When you choose to use an audio file from Google Drive, you select it through Google's file picker. We then download only that file's content so it can be processed and played in our audio looper application.</li>
            </Typography>
            <Typography
              variant="body1"
              sx={{
                lineHeight: 1.8,
                color: '#555',
                marginBottom: '1rem',
                textAlign: 'justify',
                fontWeight: 600,
              }}
            >
              What We Do With Your Google Drive Data:
            </Typography>
            <Typography
              variant="body1"
              component="ul"
              sx={{
                lineHeight: 1.8,
                color: '#555',
                marginLeft: '2rem',
                marginBottom: '1rem',
              }}
            >
              <li><strong>File Selection:</strong> When you choose to use a file from Google Drive, you explicitly select the file through Google's file picker interface. We only access files that you have explicitly selected.</li>
              <li><strong>File Download:</strong> We download only the audio files that you have selected through the Google Drive picker. The file content is downloaded temporarily to enable playback and processing in our application.</li>
              <li><strong>File Metadata:</strong> We store basic metadata about selected files (file ID, name, MIME type) in our database to enable you to access these files in future sessions.</li>
              <li><strong>No File Modification:</strong> We do not modify, delete, create, or change permissions on any files in your Google Drive. Our access is strictly read-only.</li>
              <li><strong>No Unauthorized Access:</strong> We do not access, browse, or list files in your Google Drive without your explicit selection through the file picker. We only access files that you have chosen to use.</li>
            </Typography>
            <Typography
              variant="body1"
              sx={{
                lineHeight: 1.8,
                color: '#555',
                marginBottom: '1rem',
                textAlign: 'justify',
                fontWeight: 600,
              }}
            >
              What We Do NOT Do:
            </Typography>
            <Typography
              variant="body1"
              component="ul"
              sx={{
                lineHeight: 1.8,
                color: '#555',
                marginLeft: '2rem',
                marginBottom: '1rem',
              }}
            >
              <li>We do not modify, edit, or delete any files in your Google Drive</li>
              <li>We do not create new files or folders in your Google Drive</li>
              <li>We do not change file permissions or sharing settings</li>
              <li>We do not access files that you have not explicitly selected</li>
              <li>We do not browse or list your files without your explicit action</li>
              <li>We do not share your Google Drive files with third parties</li>
              <li>We do not store your Google Drive access token permanently (tokens are stored locally in your browser and expire automatically)</li>
            </Typography>
            <Typography
              variant="body1"
              sx={{
                lineHeight: 1.8,
                color: '#555',
                marginBottom: '1rem',
                textAlign: 'justify',
                fontWeight: 600,
              }}
            >
              Your Control:
            </Typography>
            <Typography
              variant="body1"
              component="ul"
              sx={{
                lineHeight: 1.8,
                color: '#555',
                marginLeft: '2rem',
                marginBottom: '1rem',
              }}
            >
              <li>You can revoke our access to your Google Drive at any time through your <a href="https://myaccount.google.com/permissions" target="_blank" rel="noopener noreferrer" style={{ color: '#2c5aa0' }}>Google Account permissions page</a></li>
              <li>You choose which files to use through the Google Drive file picker</li>
              <li>You can disconnect Google Drive integration at any time</li>
              <li>Access tokens are stored locally in your browser and can be cleared by clearing your browser's local storage</li>
            </Typography>
            <Typography
              variant="body1"
              sx={{
                lineHeight: 1.8,
                color: '#555',
                marginBottom: '1rem',
                textAlign: 'justify',
              }}
            >
              <strong>Important:</strong> We use the <code>drive.file</code> scope so that our application can only access files you create with our app or explicitly select through the file picker. We do not browse or access the rest of your Google Drive.
            </Typography>
          </Box>

          <Box sx={{ marginBottom: '2rem' }}>
            <Typography
              variant="h5"
              component="h2"
              sx={{
                fontSize: '1.25rem',
                fontWeight: 600,
                color: '#2c5aa0',
                marginBottom: '1rem',
              }}
            >
              5. Your Rights
            </Typography>
            <Typography
              variant="body1"
              sx={{
                lineHeight: 1.8,
                color: '#555',
                marginBottom: '1rem',
                textAlign: 'justify',
              }}
            >
              You have the right to:
            </Typography>
            <Typography
              variant="body1"
              component="ul"
              sx={{
                lineHeight: 1.8,
                color: '#555',
                marginLeft: '2rem',
                marginBottom: '1rem',
              }}
            >
              <li>Access your personal information and audio files</li>
              <li>Delete your account and associated data</li>
              <li>Request correction of inaccurate information</li>
              <li>Withdraw consent for data processing (where applicable)</li>
              <li>Export your data</li>
            </Typography>
            <Typography
              variant="body1"
              sx={{
                lineHeight: 1.8,
                color: '#555',
                marginBottom: '1rem',
                textAlign: 'justify',
              }}
            >
              To exercise these rights, please contact us or use the account management features in your profile.
            </Typography>
          </Box>

          <Box sx={{ marginBottom: '2rem' }}>
            <Typography
              variant="h5"
              component="h2"
              sx={{
                fontSize: '1.25rem',
                fontWeight: 600,
                color: '#2c5aa0',
                marginBottom: '1rem',
              }}
            >
              6. Data Retention
            </Typography>
            <Typography
              variant="body1"
              sx={{
                lineHeight: 1.8,
                color: '#555',
                marginBottom: '1rem',
                textAlign: 'justify',
              }}
            >
              We retain your personal information and audio files for as long as your account is active or as needed to provide you with our services. If you delete your account, we will delete your personal information and audio files, subject to any legal obligations to retain certain information.
            </Typography>
          </Box>

          <Box sx={{ marginBottom: '2rem' }}>
            <Typography
              variant="h5"
              component="h2"
              sx={{
                fontSize: '1.25rem',
                fontWeight: 600,
                color: '#2c5aa0',
                marginBottom: '1rem',
              }}
            >
              7. Changes to This Privacy Policy
            </Typography>
            <Typography
              variant="body1"
              sx={{
                lineHeight: 1.8,
                color: '#555',
                marginBottom: '1rem',
                textAlign: 'justify',
              }}
            >
              We may update this Privacy Policy from time to time. We will notify you of any changes by posting the new Privacy Policy on this page and updating the "Last updated" date. You are advised to review this Privacy Policy periodically for any changes.
            </Typography>
          </Box>

          <Box sx={{ marginTop: '3rem', paddingTop: '2rem', borderTop: '1px solid #e0e0e0' }}>
            <Typography
              variant="body2"
              sx={{
                color: '#666',
                textAlign: 'center',
              }}
            >
              <Link
                to="/"
                style={{
                  color: '#2c5aa0',
                  textDecoration: 'none',
                }}
              >
                ← Back to Home
              </Link>
              {' | '}
              <Link
                to="/terms-of-service"
                style={{
                  color: '#2c5aa0',
                  textDecoration: 'none',
                }}
              >
                Terms of Service
              </Link>
            </Typography>
          </Box>
        </Paper>
      </Container>
    </Box>
  );
};

export default PrivacyPolicyPage;

